import { useCallback, useEffect, useState } from 'react';
import { MdOutlineHistory, MdOutlineSearch } from 'react-icons/md';
import { useToast } from '@podus/ui';
import EmptyState from '@/components/EmptyState';
import { api, ApiError } from '@/lib/api';
import { formatDateTime, humanizeAction } from '@/lib/format';
import type { AuditLogRow, Pagination } from '@/types';

const ACTOR_STYLES: Record<string, string> = {
    admin: 'bg-blue-100 text-blue-700',
    ambassador: 'bg-primary/10 text-primary',
    system: 'bg-[#f0f0f0] text-[#888]',
};

/** Read-only window onto the immutable audit trail (FR-11). */
export default function AuditLog() {
    const toast = useToast();

    const [rows, setRows] = useState<AuditLogRow[]>([]);
    const [pagination, setPagination] = useState<Pagination | null>(null);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [debounced, setDebounced] = useState('');
    const [actorType, setActorType] = useState<'' | 'admin' | 'ambassador' | 'system'>('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const timer = window.setTimeout(() => {
            setDebounced(search.trim());
            setPage(1);
        }, 350);
        return () => window.clearTimeout(timer);
    }, [search]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const query = new URLSearchParams({
                page: String(page),
                limit: '25',
                ...(debounced ? { action: debounced } : {}),
                ...(actorType ? { actorType } : {}),
            });
            const res = await api.get<{ data: AuditLogRow[]; pagination: Pagination }>(
                `/ambassador/admin/audit-logs?${query.toString()}`,
            );
            setRows(res.response.data);
            setPagination(res.response.pagination);
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : 'Could not load the audit log');
        } finally {
            setLoading(false);
        }
    }, [page, debounced, actorType, toast]);

    useEffect(() => {
        void load();
    }, [load]);

    return (
        <div className="mx-auto max-w-5xl px-4 py-6 lg:px-8 lg:py-8">
            <h2 className="text-[22px] font-bold text-secondary">Audit log</h2>
            <p className="mt-1 text-[13px] text-[#747474]">
                Append-only record of every privileged action on the programme. Entries are never edited or deleted.
            </p>

            <div className="mt-5 flex flex-wrap items-center gap-3">
                <div className="flex min-w-0 flex-1 items-center gap-2 rounded-lg border border-[#E0E0E0] bg-white px-4 py-2.5 sm:max-w-sm">
                    <MdOutlineSearch size={18} className="shrink-0 text-[#9a9a9a]" />
                    <input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Filter by action, e.g. suspend"
                        aria-label="Filter audit log by action"
                        className="min-w-0 flex-1 bg-transparent text-[14px] text-secondary outline-none placeholder:text-[#9a9a9a]"
                    />
                </div>

                <div className="flex items-center gap-1 rounded-lg border border-[#E0E0E0] bg-white p-1">
                    {(['', 'admin', 'ambassador', 'system'] as const).map((value) => (
                        <button
                            key={value || 'all'}
                            type="button"
                            onClick={() => {
                                setActorType(value);
                                setPage(1);
                            }}
                            className={`rounded-md px-3 py-1.5 text-[13px] font-medium capitalize transition-colors ${
                                actorType === value ? 'bg-secondary text-white' : 'text-[#6b6b6b] hover:bg-[#f5f5f5]'
                            }`}
                        >
                            {value || 'All'}
                        </button>
                    ))}
                </div>
            </div>

            <div className="mt-5">
                {loading ? (
                    <div className="flex flex-col gap-2">
                        {Array.from({ length: 6 }).map((_, index) => (
                            <div key={index} className="skeleton-shimmer h-16 w-full rounded-lg" />
                        ))}
                    </div>
                ) : rows.length === 0 ? (
                    <EmptyState
                        Icon={MdOutlineHistory}
                        title="Nothing logged yet"
                        description="Invitations, status changes and attribution decisions all appear here as they happen."
                    />
                ) : (
                    <ul className="flex flex-col gap-2">
                        {rows.map((row) => (
                            <li key={row.id} className="rounded-xl border border-[#E0E0E0] bg-white px-5 py-4">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span
                                            className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold capitalize ${
                                                ACTOR_STYLES[row.actorType] ?? ACTOR_STYLES.system
                                            }`}
                                        >
                                            {row.actorType}
                                        </span>
                                        <p className="text-[14px] font-semibold text-secondary">
                                            {humanizeAction(row.action)}
                                        </p>
                                        {row.targetType && (
                                            <span className="font-mono text-[12px] text-[#9a9a9a]">
                                                {row.targetType}
                                                {row.targetId ? `#${row.targetId}` : ''}
                                            </span>
                                        )}
                                    </div>
                                    <span className="text-[12px] text-[#9a9a9a]">{formatDateTime(row.createdAt)}</span>
                                </div>

                                {row.actorLabel && (
                                    <p className="mt-1 text-[13px] text-[#747474]">by {row.actorLabel}</p>
                                )}

                                {row.metadata && Object.keys(row.metadata).length > 0 && (
                                    <dl className="mt-2 flex flex-wrap gap-x-5 gap-y-1">
                                        {Object.entries(row.metadata).map(([key, value]) => (
                                            <div key={key} className="flex items-center gap-1.5">
                                                <dt className="text-[12px] text-[#9a9a9a]">{key}:</dt>
                                                <dd className="text-[12px] font-medium text-[#4E4D4D]">
                                                    {value === null || value === undefined ? '—' : String(value)}
                                                </dd>
                                            </div>
                                        ))}
                                    </dl>
                                )}
                            </li>
                        ))}
                    </ul>
                )}

                {pagination && pagination.totalPages > 1 && (
                    <div className="mt-4 flex items-center justify-between">
                        <p className="text-[13px] text-[#747474]">
                            Page {pagination.page} of {pagination.totalPages} · {pagination.total} entries
                        </p>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                disabled={page <= 1}
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                className="rounded-lg border border-[#E0E0E0] px-3 py-1.5 text-[13px] font-semibold text-secondary transition-colors hover:bg-[#f5f5f5] disabled:opacity-40"
                            >
                                Previous
                            </button>
                            <button
                                type="button"
                                disabled={page >= pagination.totalPages}
                                onClick={() => setPage((p) => p + 1)}
                                className="rounded-lg border border-[#E0E0E0] px-3 py-1.5 text-[13px] font-semibold text-secondary transition-colors hover:bg-[#f5f5f5] disabled:opacity-40"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
